"use client";

/**
 * The calls to action the site's pages close on: the dual band and the night close. Their
 * studies stay on the concept board (concept/groups/cta.tsx).
 */

import type { ReactNode } from "react";
import { HOME_COPY } from "@/lib/landing-copy";

import { DATA_LABEL_CLS, DISPLAY, EDITORIAL_ACTION_CLS, EDITORIAL_SECTION_CLS, GROUND, Grain, PARTNER_HREF, SECTION_TITLE_CLS, SectionMarker, SURFACE_DARK, WAITLIST_HREF } from "@/components/editorial";

const PRIMARY_ON_DARK = `${EDITORIAL_ACTION_CLS} bg-[var(--theme-ground)] text-[var(--theme-accent-text)] hover:bg-[color-mix(in_srgb,var(--theme-ground)_90%,var(--theme-accent))] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-accent)]`;

const OUTLINE_ON_DARK = `${EDITORIAL_ACTION_CLS} border border-[color-mix(in_srgb,var(--theme-ground)_38%,transparent)] text-[var(--theme-ground)] hover:bg-[color-mix(in_srgb,var(--theme-ground)_8%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-accent)]`;

/* ================================================================= */
/*  CTA-006 · Dual CTA band                                            */
/* ================================================================= */

const DUAL_CELLS = [
  {
    marker: "Your first workflow",
    title: "Discuss your first workflow",
    body: "Show us a recurring report, an awkward handover or a decision that takes too long to prepare. We’ll explore where your Twin could help and agree a useful starting point.",
    href: WAITLIST_HREF,
    variant: "primary",
  },
  {
    marker: "Partnership",
    title: "Become a partner",
    body: "Bring the Twin to your clients. Talk to Mercury Labs, the team behind your Twin, about delivery and partnership options.",
    href: PARTNER_HREF,
    variant: "outline",
  },
] as const;

/** `title` defaults to the home closing line; the SKT concepts pass the
 *  same words with a hand-drawn mark. */
export function DualCtaBand({ title = HOME_COPY.closing }: { title?: ReactNode } = {}) {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <Grain opacity={0.5} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section="" label="Two ways to start" dark />
        <h2
          className={`mt-8 max-w-[18ch] text-balance text-[var(--theme-ground)] ${SECTION_TITLE_CLS}`}
          style={DISPLAY}
        >
          {title}
        </h2>

        <div className="mt-12 grid gap-y-10 md:mt-16 lg:grid-cols-2 lg:gap-x-16">
          {DUAL_CELLS.map((cell) => (
            <div key={cell.title} className="flex max-w-[36rem] flex-col">
              <p className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
                {cell.marker}
              </p>
              <p className="mt-3 max-w-[40ch] flex-1 text-[1.05rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]">
                {cell.body}
              </p>
              <div className="mt-7">
                <a
                  href={cell.href}
                  className={cell.variant === "primary" ? PRIMARY_ON_DARK : OUTLINE_ON_DARK}
                  {...(cell.variant === "outline"
                    ? { target: "_blank", rel: "noreferrer" }
                    : {})}
                >
                  {cell.title}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** A quiet text link on the night surface, below the close's one action. */
const QUIET_ON_DARK =
  "inline-flex items-center gap-1 py-2.5 text-[0.88rem] font-medium text-[color-mix(in_srgb,var(--theme-ground)_78%,transparent)] underline decoration-[color-mix(in_srgb,var(--theme-ground)_35%,transparent)] underline-offset-4 hover:decoration-current";

/** What happens after the close's action, where a page gives no line of its own. */
export const CLOSING_NEXT =
  "Show us a recurring report, an awkward handover or a decision that takes too long to prepare. We agree a first workflow with you, then connect its systems and check the results with your team in two focused weeks.";

/**
 * The close with one action (the 2026-09-30 copy review): the night band,
 * its title, what happens next and one button whose label matches the
 * hero's. Partnership is a quiet link beside it rather than a second cell.
 */
export function ClosingCtaBand({
  title = HOME_COPY.closing,
  next = CLOSING_NEXT,
  cta = "Discuss your first workflow",
}: {
  title?: ReactNode;
  next?: string;
  cta?: string;
} = {}) {
  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: SURFACE_DARK, color: GROUND }}>
      <Grain opacity={0.5} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section="" label="Your first workflow" dark />
        <h2 className={`mt-8 max-w-[18ch] text-balance text-[var(--theme-ground)] ${SECTION_TITLE_CLS}`} style={DISPLAY}>
          {title}
        </h2>
        <p className="mt-6 max-w-[52ch] text-[1.05rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]">
          {next}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3">
          <a href={WAITLIST_HREF} data-cta-placement="closing" className={PRIMARY_ON_DARK}>
            {cta}
          </a>
          <a href={PARTNER_HREF} target="_blank" rel="noreferrer" className={QUIET_ON_DARK}>
            Partner with Mercury Labs <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    </section>
  );
}
